import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Select,
  TextField,
  DataTable,
  Text,
  Badge,
  Button,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

const TYPE_OPTIONS = [
  { label: 'All Overdue', value: '' },
  { label: 'Direct Rentals', value: 'direct' },
  { label: 'Clinic Rentals', value: 'clinic' },
  { label: 'Demo / Loaner', value: 'demo' },
  { label: 'Consignment', value: 'consignment' },
];

export default function OverdueReport() {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  // Empty by default: no date bounds means every overdue rental, which is the
  // right thing to see first on this particular report.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const reportParams = {
    ...(type && { type }),
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  };

  const { data: report, isLoading } = useQuery({
    queryKey: ['report-overdue', type, startDate, endDate],
    queryFn: () => reportsApi.getOverdue(reportParams),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Export what is on screen, dates included — exporting a different set from
      // the one being looked at is worse than not exporting at all.
      await reportsApi.downloadCsv('/reports/overdue', reportParams);
    } finally {
      setExporting(false);
    }
  };

  const rows = (report?.data || []).map((item) => [
    <Button key={item.rentalId} variant="plain" onClick={() => navigate(`/rentals/${item.rentalId}`)}>
      {item.orderNumber || item.rentalId.slice(0, 8)}
    </Button>,
    item.customerName || 'N/A',
    item.clinicName || '—',
    item.productName || '—',
    item.endDate ? new Date(item.endDate).toLocaleDateString() : '—',
    <Badge key={`days-${item.rentalId}`} tone={item.daysOverdue > 14 ? 'critical' : 'warning'}>
      {`${item.daysOverdue} days`}
    </Badge>,
    `$${Number(item.amount).toFixed(2)}`,
    <Badge key={`type-${item.rentalId}`}>
      {item.orderType}
    </Badge>,
  ]);

  return (
    <Page
      title="Overdue Rentals Report"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="400" blockAlign="end">
            <Box minWidth="250px">
              <Select
                label="Filter by type"
                options={TYPE_OPTIONS}
                value={type}
                onChange={setType}
              />
            </Box>
            {/* Both dates start empty on purpose. The other reports default to the
                last 30 days, but doing that here would open the page with the
                longest-overdue rentals already hidden. */}
            <TextField
              label="Due from"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="Due to"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            {(startDate || endDate) && (
              <Button
                variant="plain"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear dates
              </Button>
            )}
            <Button onClick={handleExport} loading={exporting}>
              Export CSV
            </Button>
          </InlineStack>
        </Card>

        {isLoading ? (
          <Card>
            <SkeletonBodyText lines={8} />
          </Card>
        ) : !report || report.data.length === 0 ? (
          <Card>
            <EmptyState
              heading="No overdue rentals"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No overdue rentals found for the selected filter.</p>
            </EmptyState>
          </Card>
        ) : (
          <>
            <InlineGrid columns={3} gap="400">
              <StatCard label="Total Overdue" value={String(report.summary.totalOverdue)} />
              <StatCard label="Total Value" value={`$${Number(report.summary.totalValue).toFixed(2)}`} />
              <StatCard label="Avg Days Overdue" value={`${report.summary.avgDaysOverdue} days`} />
            </InlineGrid>

            <Card>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Customer', 'Clinic', 'Product', 'End Date', 'Overdue', 'Amount', 'Type']}
                rows={rows}
              />
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}
